import { useMemo, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import DetailedMetricRow from "../../../core/blocks/DetailedMetricRow";
import ContentIcon from "../../../../assets/images/icons/product.svg";
import ThemeLine from "../ThemeLine";
import { useAppSelector } from "../../../../redux/hooks";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4CategoryReportByDate } from "../../../../modules/perf_reports/api/category/v4CategoryReportByDate";
import { useGetV4CategoryReportTotals } from "../../../../modules/perf_reports/api/category/v4CategoryReportTotals";

interface ContentCardProps {
  category?: string | null;
}

const ContentCard = ({ category = null }: ContentCardProps): ReactElement => {
  const { t } = useTranslation(["merchandising"]);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  // Fetch category data
  const categoryByDateOptionsGetter = useGetV4CategoryReportByDate();
  const categoryByDateReportQuery = useQuery({
    ...categoryByDateOptionsGetter({
      filters: category ? [{ id: "category_id", value: { op: "=", value: category } }] : [],
      search: "",
    }),
  });

  const categoryTotalOptionsGetter = useGetV4CategoryReportTotals();
  const categoryTotalsReportQuery = useQuery({
    ...categoryTotalOptionsGetter({
      filters: category ? [{ id: "category_id", value: { op: "=", value: category } }] : [],
      search: "",
    }),
  });

  const data = useMemo(() => {
    if (categoryTotalsReportQuery.data?.data && categoryByDateReportQuery.data?.data) {
      return {
        total: categoryTotalsReportQuery.data.data,
        date: categoryByDateReportQuery.data.data,
      };
    }
    return null;
  }, [categoryTotalsReportQuery.data?.data, categoryByDateReportQuery.data?.data]);

  const metrics = useMemo(() => {
    if (channel === "shopify") {
      return [];
    }

    return [
      getMetricSpec("seo_score"),
      getMetricSpec("ctr"),
      getMetricSpec("buyability_score"),
      getMetricSpec("conversion_rate"),
    ];
  }, [channel]);

  return (
    <DetailedMetricRow
      title={t(($) => $.merchandising["Content"])}
      to={`/user/content/quality/${category ? `category/${category}` : "org"}`}
      byline={
        <>
          <ThemeLine>{t(($) => $.merchandising["Product"])}</ThemeLine>
          {t(($) => $.merchandising["Measure product content quality and conversion rate"])}
        </>
      }
      totals={{ data: data?.total, loading: categoryTotalsReportQuery.isLoading }}
      timeline={{ data: data?.date, loading: categoryByDateReportQuery.isLoading }}
      metrics={metrics}
      icon={ContentIcon}
    />
  );
};

export default ContentCard;
